<template>
  <div>
    <div class="page-head">
      <h1>Subject Catalog</h1>
      <p>Enroll subjects from the DSA pool for your students. <b>Enrollment is open for the current quarter only</b>.</p>
    </div>

    <div class="view-tabs">
      <button
        type="button"
        class="vtab"
        :class="{ active: viewMode === 'discipline' }"
        @click="setViewMode('discipline')"
      >By Discipline</button>
      <button
        type="button"
        class="vtab"
        :class="{ active: viewMode === 'window' }"
        @click="setViewMode('window')"
      >By Window</button>
    </div>

    <div v-if="quota && currentQuarterLabel" class="notice" :class="{ warn: quotaExhausted }">
      <b>{{ currentQuarterLabel }} · {{ quota.used }} of {{ quota.limit }} subjects enrolled · {{ quota.remaining }} remaining</b>
      — enrollment locks your quarterly quota; applications cannot be cancelled or swapped once submitted.
    </div>

    <template v-if="viewMode === 'window'">
      <div v-if="bulkMode && currentBlock" class="bulk-bar">
        <span>{{ selectedCodes.length }} selected</span>
        <button
          type="button"
          class="btn btn-primary btn-sm"
          :disabled="enrolling || !selectedCodes.length"
          @click="enrollSelected"
        >
          {{ enrolling ? 'Enrolling…' : 'Enroll selected' }}
        </button>
        <button type="button" class="btn btn-outline btn-sm" @click="toggleBulk(false)">Cancel</button>
      </div>
      <div v-else-if="currentBlock && quota && quota.remaining > 0" class="bulk-toggle">
        <button type="button" class="entry" @click="toggleBulk(true)">Bulk enroll subjects</button>
      </div>
      <div class="filters">
        <button
          v-for="cat in categories"
          :key="cat"
          type="button"
          class="fbtn"
          :class="{ active: activeCategory === cat }"
          @click="activeCategory = cat"
        >{{ cat }}</button>
      </div>
    </template>

    <dsa-loading v-if="loading" text="Loading catalog…" />

    <template v-else-if="viewMode === 'discipline'">
      <div v-if="!disciplineSubjects.length" class="empty">
        <h3>No subjects configured</h3>
        <p>Ask DSA admin to add subjects in the master catalog.</p>
      </div>
      <div v-else class="wgrid discipline-grid">
        <div class="wcard disc-col">
          <div class="wcard-head"><h3>Category</h3></div>
          <button
            v-for="cat in categories"
            :key="cat"
            type="button"
            class="disc-item"
            :class="{ active: selectedCategory === cat }"
            @click="selectCategory(cat)"
          >{{ cat }}</button>
        </div>

        <div class="wcard disc-col">
          <div class="wcard-head"><h3>Major</h3></div>
          <div v-if="!majorsForCategory.length" class="disc-empty">No majors in this category</div>
          <button
            v-for="major in majorsForCategory"
            :key="major"
            type="button"
            class="disc-item"
            :class="{ active: selectedMajor === major }"
            @click="selectMajor(major)"
          >{{ major }}</button>
        </div>

        <div class="wcard disc-col">
          <div class="wcard-head"><h3>Subject</h3></div>
          <div v-if="!subjectsForMajor.length" class="disc-empty">No subjects in this major</div>
          <div
            v-for="sub in subjectsForMajor"
            :key="sub.id"
            class="disc-subject"
            :class="{ active: selectedSubject && selectedSubject.id === sub.id }"
            @click="selectSubject(sub)"
          >
            <div class="sname">{{ sub.subject }}</div>
            <div class="disc-subject-tags">
              <span
                v-if="subjectWindowLabel(sub) !== '—'"
                class="cycle-tag"
              >{{ subjectWindowLabel(sub) }}</span>
              <span
                v-if="isEnrollmentOpen(sub)"
                class="chip open disc-open-tag"
              >Enrollment Open</span>
              <span
                v-else-if="sub.registered_by_institution"
                class="chip open disc-open-tag"
              >Enrolled</span>
            </div>
          </div>
        </div>

        <div class="wcard disc-col disc-detail">
          <div class="wcard-head"><h3>Details</h3></div>
          <div v-if="!selectedSubject" class="disc-empty">Select a subject to view details</div>
          <template v-else>
            <h4 class="detail-title">{{ selectedSubject.subject }}</h4>
            <div class="detail-block">
              <div class="label">Intro</div>
              <div class="detail-intro">{{ selectedSubject.intro || '—' }}</div>
            </div>
            <div class="detail-meta">
              <div>
                <div class="label">Window</div>
                <div class="val">{{ subjectWindowLabel(selectedSubject) }}</div>
              </div>
              <div>
                <div class="label">Exam date</div>
                <div class="val">{{ selectedSubject.exam_date || '—' }}</div>
              </div>
              <div>
                <div class="label">Status</div>
                <div class="val">
                  <span class="chip" :class="statusChip(selectedSubject.status)">{{ selectedSubject.status_label }}</span>
                </div>
              </div>
            </div>
            <div v-if="(selectedSubject.resource_list || []).length" class="detail-block">
              <div class="label">Resources</div>
              <ul class="resource-list">
                <li v-for="(r, idx) in selectedSubject.resource_list" :key="idx">
                  <a
                    v-if="r.url"
                    :href="r.url"
                    target="_blank"
                    rel="noopener noreferrer"
                  >{{ r.title || r.url }}</a>
                  <span v-else>{{ r.title || 'Resource' }}</span>
                </li>
              </ul>
            </div>
            <div class="detail-actions">
              <button
                v-if="showDetailEnrollButton(selectedSubject)"
                type="button"
                class="btn btn-primary"
                :disabled="enrolling || !canEnrollSubject(selectedSubject)"
                @click="openEnrollConfirm(selectedSubject)"
              >Enroll</button>
            </div>
          </template>
        </div>
      </div>
    </template>

    <template v-else>
      <div v-if="!quarterBlocks.length" class="empty">
        <h3>No quarters available</h3>
        <p>Ask DSA admin to configure active quarters and subjects.</p>
      </div>
      <div v-else class="wgrid">
        <div
          v-for="block in quarterBlocks"
          :key="block.quarter_code"
          class="wcard"
          :class="block.isCurrent ? 'current' : 'upcoming'"
        >
          <div class="wcard-head">
            <h3>{{ block.label }}</h3>
            <span v-if="block.isCurrent" class="chip open">Current Window</span>
          </div>
          <div class="wcard-dates"><b>{{ block.examHint }}</b><br>{{ block.regHint }}</div>
          <div v-if="filteredSubjects(block).length === 0" class="empty" style="padding:14px 0;border:none">No subjects in this category</div>
          <div v-for="sub in filteredSubjects(block)" :key="sub.id" class="subject-row">
            <div class="srow-main">
              <label v-if="bulkMode && block.registration_open && !sub.registered_by_institution && sub.can_enroll" class="bulk-check">
                <input type="checkbox" :value="sub.dsa_subject_code" v-model="selectedCodes">
              </label>
              <div>
                <span class="sname">{{ sub.subject || sub.dsa_subject_name }}</span>
                <span class="scat">{{ sub.category || subjectCategory(sub) }}</span>
              </div>
            </div>
            <div class="sact">
              <span v-if="sub.registered_by_institution" class="chip open">Enrolled</span>
              <button
                v-else-if="sub.can_enroll"
                type="button"
                class="btn btn-primary btn-sm"
                :disabled="enrolling"
                @click="openEnrollConfirm(sub)"
              >Enroll</button>
              <span v-else-if="block.isCurrent && !sub.can_enroll && !sub.registered_by_institution" class="chip pend">{{ sub.status_label || 'Not open' }}</span>
            </div>
          </div>
        </div>
      </div>
    </template>

    <div v-if="confirmOpen" class="modal-overlay" @click.self="closeConfirm">
      <div class="modal-card">
        <h3>Confirm Subject Enrollment</h3>
        <p>
          Enroll <b>{{ confirmSubjectName }}</b> for {{ confirmQuarterLabel }}?
          This will consume {{ confirmCount }} subject slot{{ confirmCount === 1 ? '' : 's' }}.
        </p>
        <div class="quota-summary">
          <div><span>Current remaining</span><b>{{ quotaRemaining }}</b></div>
          <div><span>After enrollment</span><b>{{ confirmRemainingAfter }}</b></div>
        </div>
        <p class="hint">Enrollment locks your quarterly quota. Applications cannot be cancelled or swapped once submitted.</p>
        <div class="actions">
          <button type="button" class="btn btn-outline" :disabled="enrolling" @click="closeConfirm">Cancel</button>
          <button type="button" class="btn btn-primary" :disabled="enrolling || confirmRemainingAfter < 0" @click="confirmEnroll">
            {{ enrolling ? 'Enrolling…' : 'Confirm' }}
          </button>
        </div>
      </div>
    </div>

    <div class="rule-card"><b>How it works:</b> your students will only see the subjects you enroll. Each subject is offered once per year in its assigned window. Enrollment opens only for the current cycle of that window.</div>
  </div>
</template>

<script>
import {
  getInstitutionDisciplineCatalog,
  submitInstitutionSubjects,
} from '@/api/dsa'
import { getInstitutionAccountId } from '@/utils/store'
import {
  subjectCategory,
  SUBJECT_CATEGORIES,
  buildCatalogQuarters,
  formatExamHint,
  formatRegistrationHint,
  formatQuarterWindow,
} from '@/utils/dsaFormat'

export default {
  name: 'InstitutionCatalog',
  data() {
    return {
      loading: true,
      enrolling: false,
      viewMode: 'discipline',
      bulkMode: false,
      selectedCodes: [],
      quarterBlocks: [],
      quota: null,
      currentQuarterLabel: '',
      activeCategory: SUBJECT_CATEGORIES[0],
      categories: SUBJECT_CATEGORIES,
      disciplineSubjects: [],
      selectedCategory: SUBJECT_CATEGORIES[0],
      selectedMajor: '',
      selectedSubject: null,
      confirmOpen: false,
      confirmSubject: null,
      confirmCodes: [],
      confirmQuarter: '',
      confirmQuarterLabel: '',
    }
  },
  computed: {
    currentBlock() {
      return this.quarterBlocks.find(b => b.isCurrent)
    },
    majorsForCategory() {
      const set = new Set()
      this.disciplineSubjects.forEach((s) => {
        if ((s.category || '') === this.selectedCategory && s.major) {
          set.add(s.major)
        }
      })
      return Array.from(set)
    },
    subjectsForMajor() {
      return this.disciplineSubjects.filter(s =>
        (s.category || '') === this.selectedCategory
        && (s.major || '') === this.selectedMajor
      )
    },
    quotaRemaining() {
      if (!this.quota) return 0
      return Number(this.quota.remaining || 0)
    },
    quotaExhausted() {
      return !!(this.quota && this.quotaRemaining <= 0)
    },
    confirmCount() {
      return this.confirmCodes.length || 1
    },
    confirmSubjectName() {
      if (this.confirmSubject) {
        return this.confirmSubject.subject || this.confirmSubject.dsa_subject_name || this.confirmSubject.dsa_subject_code
      }
      return `${this.confirmCount} selected subjects`
    },
    confirmRemainingAfter() {
      return this.quotaRemaining - this.confirmCount
    },
  },
  mounted() {
    this.load()
  },
  methods: {
    subjectCategory,
    formatCycle(code) {
      return formatQuarterWindow(code)
    },
    /** Prefer matched cycle with year (e.g. 2026 Q3); fall back to window code. */
    subjectWindowLabel(sub) {
      if (!sub) return '—'
      if (sub.next_cycle_label) return sub.next_cycle_label
      const cycleCode = sub.next_cycle_code || sub.quarter_code
      if (cycleCode) return formatQuarterWindow(cycleCode)
      return sub.window_label || sub.window || '—'
    },
    isEnrollmentOpen(sub) {
      if (!sub || sub.registered_by_institution) return false
      return !!(sub.enrollment_open || sub.can_enroll || sub.status === 'open')
    },
    subjectQuarterCode(sub) {
      return String((sub && (sub.quarter_code || sub.next_cycle_code)) || '').toUpperCase()
    },
    isCurrentQuarterSubject(sub) {
      const current = this.currentBlock && this.currentBlock.quarter_code
      return !!current && this.subjectQuarterCode(sub) === String(current).toUpperCase()
    },
    canEnrollSubject(sub) {
      return !!(sub && sub.can_enroll && !sub.registered_by_institution && this.quotaRemaining > 0)
    },
    showDetailEnrollButton(sub) {
      if (!sub || sub.registered_by_institution) return false
      return this.canEnrollSubject(sub) || (this.quotaExhausted && this.isCurrentQuarterSubject(sub))
    },
    statusChip(status) {
      if (status === 'enrolled' || status === 'open') return 'open'
      return 'pend'
    },
    setViewMode(mode) {
      this.viewMode = mode
      if (mode === 'window') {
        this.toggleBulk(false)
      }
    },
    filteredSubjects(block) {
      return (block.subjects || []).filter(s => (s.category || subjectCategory(s)) === this.activeCategory)
    },
    selectCategory(cat) {
      this.selectedCategory = cat
      const majors = this.majorsForCategory
      this.selectedMajor = majors[0] || ''
      this.selectedSubject = this.subjectsForMajor[0] || null
    },
    selectMajor(major) {
      this.selectedMajor = major
      this.selectedSubject = this.subjectsForMajor[0] || null
    },
    selectSubject(sub) {
      this.selectedSubject = sub
    },
    syncDisciplineSelection() {
      if (!this.categories.includes(this.selectedCategory)) {
        this.selectedCategory = this.categories[0]
      }
      const majors = this.majorsForCategory
      if (!majors.includes(this.selectedMajor)) {
        this.selectedMajor = majors[0] || ''
      }
      const subjects = this.subjectsForMajor
      if (!this.selectedSubject || !subjects.find(s => s.id === this.selectedSubject.id)) {
        this.selectedSubject = subjects[0] || null
      }
    },
    buildWindowBlocks(quarters, subjects) {
      const catalogQuarters = buildCatalogQuarters(quarters || [])
      if (!catalogQuarters.length) {
        this.quarterBlocks = []
        return
      }
      const byQuarter = {}
      subjects.forEach((sub) => {
        const code = String(sub.quarter_code || sub.next_cycle_code || '').toUpperCase()
        if (!code) return
        if (!byQuarter[code]) byQuarter[code] = []
        byQuarter[code].push(sub)
      })
      this.quarterBlocks = catalogQuarters.map((q) => {
        const code = String(q.quarter_code || '').toUpperCase()
        const isCurrent = !!q.isCurrent
        return {
          quarter_code: code,
          label: q.quarter_name || q.quarter_code,
          isCurrent,
          registration_open: !!q.registration_open,
          examHint: formatExamHint(q),
          regHint: formatRegistrationHint(q, isCurrent, { enrollment: true }),
          subjects: byQuarter[code] || [],
        }
      })
      const cur = this.quarterBlocks.find(b => b.isCurrent)
      if (cur && !this.currentQuarterLabel) {
        this.currentQuarterLabel = cur.label
      }
    },
    async load() {
      const account_id = getInstitutionAccountId()
      if (!account_id) return
      this.loading = true
      try {
        const res = await getInstitutionDisciplineCatalog(account_id)
        if (!res.data || res.data.code !== 0) {
          this.disciplineSubjects = []
          this.quarterBlocks = []
          this.$emit('toast', (res.data && res.data.msg) || 'Failed to load catalog')
          return
        }
        const data = res.data.data || {}
        this.disciplineSubjects = data.subjects || []
        if (Array.isArray(data.categories) && data.categories.length) {
          this.categories = data.categories
        }
        if (data.quota) {
          this.quota = data.quota
        }
        if (data.current_quarter_label) {
          this.currentQuarterLabel = data.current_quarter_label
        } else if (data.current_quarter_code) {
          this.currentQuarterLabel = formatQuarterWindow(data.current_quarter_code)
        }
        this.buildWindowBlocks(data.quarters || [], this.disciplineSubjects)
        this.syncDisciplineSelection()
      } catch (e) {
        this.$emit('toast', 'Failed to load catalog')
      } finally {
        this.loading = false
      }
    },
    toggleBulk(on) {
      this.bulkMode = on
      if (!on) this.selectedCodes = []
    },
    applySuccessfulEnrollment(quarter, codes = []) {
      const enrolled = new Set(codes.map(c => String(c).toUpperCase()))
      this.disciplineSubjects.forEach((s) => {
        if (enrolled.has(String(s.dsa_subject_code || '').toUpperCase())) {
          s.registered_by_institution = true
          s.can_enroll = false
          s.status = 'enrolled'
          s.status_label = 'Enrolled'
        } else if (this.quotaRemaining <= codes.length && this.subjectQuarterCode(s) === String(quarter).toUpperCase()) {
          s.can_enroll = false
        }
      })
      if (this.quota) {
        const count = codes.length
        const used = Number(this.quota.used || 0) + count
        const remaining = Math.max(0, Number(this.quota.remaining || 0) - count)
        this.quota = { ...this.quota, used, remaining }
      }
      this.syncDisciplineSelection()
    },
    openEnrollConfirm(sub) {
      if (!this.canEnrollSubject(sub)) return
      const quarter = this.subjectQuarterCode(sub)
      if (!quarter) {
        this.$emit('toast', 'No open cycle found for this subject window')
        return
      }
      this.confirmSubject = sub
      this.confirmCodes = [sub.dsa_subject_code]
      this.confirmQuarter = quarter
      this.confirmQuarterLabel = this.formatCycle(quarter) || quarter
      this.confirmOpen = true
    },
    closeConfirm() {
      if (this.enrolling) return
      this.resetConfirm()
    },
    resetConfirm() {
      this.confirmOpen = false
      this.confirmSubject = null
      this.confirmCodes = []
      this.confirmQuarter = ''
      this.confirmQuarterLabel = ''
    },
    async confirmEnroll() {
      const account_id = getInstitutionAccountId()
      if (!account_id || !this.confirmQuarter || !this.confirmCodes.length) return
      if (this.confirmRemainingAfter < 0) {
        this.$emit('toast', `Only ${this.quotaRemaining} subject slot(s) remaining`)
        return
      }
      this.enrolling = true
      try {
        const res = await submitInstitutionSubjects(account_id, this.confirmQuarter, this.confirmCodes)
        if (res.data && res.data.code === 0) {
          const isBulk = this.confirmCodes.length > 1
          this.$emit('toast', isBulk ? 'Subjects enrolled successfully' : 'Subject enrolled successfully')
          const codes = [...this.confirmCodes]
          const quarter = this.confirmQuarter
          this.resetConfirm()
          this.applySuccessfulEnrollment(quarter, codes)
          if (isBulk) this.toggleBulk(false)
        } else {
          this.$emit('toast', (res.data && res.data.msg) || 'Enrollment failed')
        }
      } catch (e) {
        this.$emit('toast', 'Enrollment failed')
      } finally {
        this.enrolling = false
      }
    },
    async enrollSelected() {
      const block = this.currentBlock
      const account_id = getInstitutionAccountId()
      if (!block || !account_id || !this.selectedCodes.length) return
      const remaining = (this.quota && this.quota.remaining) || 0
      if (this.selectedCodes.length > remaining) {
        this.$emit('toast', `Only ${remaining} subject slot(s) remaining`)
        return
      }
      this.confirmSubject = null
      this.confirmCodes = [...this.selectedCodes]
      this.confirmQuarter = block.quarter_code
      this.confirmQuarterLabel = block.label
      this.confirmOpen = true
    },
    async enrollDisciplineSubject(sub) {
      this.openEnrollConfirm(sub)
    },
  },
}
</script>

<style scoped>
.view-tabs{display:flex;gap:0;margin-bottom:18px;border-bottom:1px solid var(--line)}
.vtab{border:none;background:transparent;padding:10px 18px;font-size:14px;font-weight:600;color:var(--muted);cursor:pointer;border-bottom:2px solid transparent;margin-bottom:-1px}
.vtab.active{color:var(--blue);border-bottom-color:var(--blue)}
.bulk-bar{display:flex;align-items:center;gap:12px;margin-bottom:16px;flex-wrap:wrap;font-size:14px}
.bulk-toggle{margin-bottom:14px}
.srow-main{display:flex;align-items:flex-start;gap:10px}
.bulk-check{margin:0;flex-shrink:0}
/* Mirror By Window (1.5fr 1fr 1fr 1fr): left 3 cols = its right 3; Details = its first col */
.discipline-grid.wgrid{
  grid-template-columns:1fr 1fr 1fr 1.5fr;
}
.disc-col{min-height:360px}
.disc-col.disc-detail{min-width:0}
.disc-item{display:block;width:100%;text-align:left;border:none;background:transparent;padding:10px 10px;border-radius:8px;font-size:13px;font-weight:500;color:var(--ink);cursor:pointer;margin-bottom:2px}
.disc-item:hover{background:#f1f5f9}
.disc-item.active{background:var(--blue-light,#e8f1fb);color:var(--blue);font-weight:600}
.disc-empty{font-size:13px;color:var(--muted);padding:12px 4px}
.disc-subject{padding:10px 8px;border-radius:8px;cursor:pointer;border-top:1px solid var(--line)}
.disc-subject:first-of-type{border-top:none}
.disc-subject:hover{background:#f8fafc}
.disc-subject.active{background:var(--blue-light,#e8f1fb)}
.disc-subject .sname{display:block;font-weight:500;line-height:1.35;font-size:13px}
.disc-subject-tags{display:flex;flex-wrap:wrap;align-items:center;gap:5px;margin-top:5px}
.cycle-tag{
  display:inline-flex;align-items:center;
  border-radius:999px;padding:1px 7px;font-size:10px;font-weight:600;
  background:var(--blue-light,#e8f1fb);color:var(--blue);letter-spacing:.01em;
}
.disc-open-tag{font-size:10px;padding:1px 7px}
.detail-title{font-size:16px;font-weight:700;margin:0 0 14px}
.detail-block{margin-bottom:14px}
.detail-block .label,
.detail-meta .label{font-size:12px;color:var(--muted);font-weight:500;margin-bottom:4px}
.detail-intro{font-size:13.5px;line-height:1.55;color:var(--gray);white-space:pre-wrap}
.detail-meta{display:grid;grid-template-columns:1fr;gap:12px;margin-bottom:14px}
.detail-meta .val{font-size:14px;font-weight:600}
.resource-list{margin:0;padding-left:18px;font-size:13.5px}
.resource-list a{color:var(--blue)}
.detail-actions{margin-top:16px}
.modal-overlay{position:fixed;inset:0;background:rgba(0,0,0,.4);display:flex;align-items:center;justify-content:center;z-index:1000;padding:16px}
.modal-card{background:#fff;border-radius:14px;padding:24px;max-width:460px;width:100%;box-shadow:0 12px 40px rgba(15,23,42,.16)}
.modal-card h3{font-size:18px;font-weight:700;margin:0 0 8px}
.modal-card p{font-size:14px;color:var(--muted);margin:0 0 16px;line-height:1.55}
.modal-card p b{color:var(--ink);font-weight:600}
.quota-summary{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin:14px 0}
.quota-summary div{border:1px solid var(--line);border-radius:10px;padding:12px;background:#fafbfd}
.quota-summary span{display:block;font-size:12px;color:var(--muted);margin-bottom:2px}
.quota-summary b{font-size:22px;color:var(--ink)}
.modal-card .hint{font-size:12.5px}
@media(max-width:1100px){
  .discipline-grid.wgrid{grid-template-columns:1fr 1fr}
}
@media(max-width:700px){
  .discipline-grid.wgrid{grid-template-columns:1fr}
}
</style>
